"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useUserUsage } from "@/hooks/useUserUsage";
import { API_BASE_URL, ONBOARDING_CALENDLY_URL } from "@/lib/config";

type MeetingStatus = "not_booked" | "booked" | "attended" | null;

function formatMeetingTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  const date = d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });

  if (diffDays <= 0) return `Today at ${time}`;
  if (diffDays === 1) return `Tomorrow at ${time}`;
  return `${date} at ${time}`;
}

export function OnboardingMeetingGate() {
  const { user } = useAuth();
  const { data: usageData } = useUserUsage();
  const searchParams = useSearchParams();
  const [status, setStatus] = useState<MeetingStatus | undefined>(undefined);
  const [meetingTime, setMeetingTime] = useState<string | null>(null);
  const [booking, setBooking] = useState(false);
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const calendlyLoaded = useRef(false);

  // The 1-on-1 onboarding call is an Elite-tier benefit only. Trial-started
  // checkouts and lower-tier purchases never see this gate. We additionally
  // verify the live tier from /usage so a hand-crafted URL with
  // ?survey=post_purchase&plan=elite cannot trigger the gate for non-Elite
  // accounts. Enterprise users also pass.
  const planParam = searchParams.get("plan");
  const liveTier = usageData?.subscription?.tier;
  const isEliteOrEnterprise =
    Boolean(usageData?.is_enterprise) || liveTier === "elite";
  const justPaid =
    searchParams.get("survey") === "post_purchase" &&
    planParam === "elite" &&
    isEliteOrEnterprise;
  const dismissKey = user?.uid
    ? `optional_onboarding_meeting_dismissed:${user.uid}:purchase`
    : null;

  // Fetch meeting status from backend (activate gate first if user just paid)
  useEffect(() => {
    if (!user?.uid || !justPaid) {
      setStatus(null);
      return;
    }

    if (dismissKey && localStorage.getItem(dismissKey) === "true") {
      setDismissed(true);
      setStatus(null);
      return;
    }

    const check = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/onboarding/meeting-status?user_id=${encodeURIComponent(user.uid)}`
        );
        if (!res.ok) {
          setStatus(null);
          return;
        }
        const data = await res.json();
        setStatus(data.status ?? null);
        if (data.meeting_start_time) setMeetingTime(data.meeting_start_time);
      } catch {
        setStatus(null);
      }
    };

    check();
  }, [user?.uid, justPaid, dismissKey]);

  // Listen for Calendly postMessage (booking confirmed)
  useEffect(() => {
    if (!justPaid || status === "booked" || status === "attended") return;

    const handleMessage = async (e: MessageEvent) => {
      if (e.data?.event === "calendly.event_scheduled") {
        setBooking(true);
        setCalendlyOpen(false);
        const eventUri = e.data?.payload?.event?.uri || null;

        try {
          const res = await fetch(`${API_BASE_URL}/onboarding/meeting-booked`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id: user?.uid,
              calendly_event_uri: eventUri,
            }),
          });
          const data = await res.json();
          if (data.meeting_start_time) setMeetingTime(data.meeting_start_time);
        } catch {}

        setStatus("booked");
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [status, user?.uid, justPaid]);

  // Detect when Calendly popup is closed (no postMessage event exists for this)
  // Poll the DOM for the Calendly overlay element — when it disappears, show our modal again
  useEffect(() => {
    if (!calendlyOpen) return;

    const interval = setInterval(() => {
      const overlay = document.querySelector(".calendly-overlay");
      if (!overlay) {
        setCalendlyOpen(false);
      }
    }, 300);

    return () => clearInterval(interval);
  }, [calendlyOpen]);

  // Preload Calendly script
  useEffect(() => {
    if (!justPaid || status === "booked" || status === "attended" || calendlyLoaded.current) return;
    if (typeof window === "undefined") return;

    if (!document.getElementById("xynth-calendly-css")) {
      const link = document.createElement("link");
      link.id = "xynth-calendly-css";
      link.rel = "stylesheet";
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      document.head.appendChild(link);
    }

    if (!document.getElementById("xynth-calendly-script")) {
      const script = document.createElement("script");
      script.id = "xynth-calendly-script";
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      script.onload = () => { calendlyLoaded.current = true; };
      document.body.appendChild(script);
    } else {
      calendlyLoaded.current = true;
    }
  }, [status, justPaid]);

  const handleBookCall = useCallback(() => {
    if (typeof window === "undefined") return;

    const win = window as any;

    // Hide our modal so Calendly popup has full control
    setCalendlyOpen(true);

    if (!win.Calendly?.initPopupWidget) {
      window.open(ONBOARDING_CALENDLY_URL, "_blank", "noopener,noreferrer");
      return;
    }

    const name = user?.displayName || user?.email?.split("@")[0] || "";
    win.Calendly.initPopupWidget({
      url: `${ONBOARDING_CALENDLY_URL}?hide_gdpr_banner=1`,
      prefill: { name, email: user?.email || undefined },
    });
  }, [user?.displayName, user?.email]);

  const handleDismiss = useCallback(() => {
    if (dismissKey) localStorage.setItem(dismissKey, "true");
    setDismissed(true);
  }, [dismissKey]);

  // Don't render until we know the status
  if (status === undefined) return null;
  // Old user or manually unlocked by founder
  if (!justPaid || dismissed || status === "attended") return null;
  // Calendly popup is open — hide our modal so it doesn't block clicks
  if (calendlyOpen) return null;

  const firstName = user?.displayName?.split(" ")[0] || "";
  const isBooked = status === "booked";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
        className="relative w-[92vw] max-w-[460px] rounded-2xl bg-black border border-white/[0.1] overflow-hidden"
      >
        <div className="px-8 pt-10 pb-9 flex flex-col items-center text-center">
          {/* Green pulsing dot */}
          <div className="relative flex h-3 w-3 mb-7">
            <span
              className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"
              style={{ animationDuration: "1.5s" }}
            />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400" />
          </div>

          {isBooked ? (
            <>
              <h2 className="text-[24px] sm:text-[28px] font-semibold text-white tracking-tight leading-tight">
                You&apos;re all set{firstName ? `, ${firstName}` : ""}.
              </h2>

              <p className="mt-4 text-[15px] text-zinc-400 leading-relaxed max-w-[380px]">
                Your call is booked. We&apos;ll walk you through alerts, research
                workflows, and your best setup live. Check your email for the invite.
              </p>

              <div className="mt-8 flex flex-col items-center gap-3">
                <div className="flex items-center gap-2.5 px-5 py-3 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.06]">
                  <div className="relative flex h-2 w-2">
                    <span
                      className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"
                      style={{ animationDuration: "2s" }}
                    />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                  </div>
                  <span className="text-[14px] text-emerald-300 font-medium">
                    Session booked
                  </span>
                </div>

                {meetingTime && (
                  <div className="px-5 py-2.5 rounded-xl border border-white/[0.08] bg-white/[0.03]">
                    <span className="text-[14px] text-zinc-300">
                      {formatMeetingTime(meetingTime)}
                    </span>
                  </div>
                )}
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleDismiss}
                className="mt-7 w-full max-w-[320px] flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-xl bg-white text-black text-[15px] font-semibold hover:bg-zinc-100 transition-colors"
              >
                Continue to Xynth
              </motion.button>
            </>
          ) : (
            <>
              <h2 className="text-[24px] sm:text-[28px] font-semibold text-white tracking-tight leading-tight">
                {firstName ? `Welcome to Elite, ${firstName}.` : "Welcome to Elite."}
              </h2>

              <p className="mt-4 text-[15px] text-zinc-400 leading-relaxed max-w-[380px]">
                <span className="font-semibold text-zinc-200">
                  1-on-1 trading session with our founders.
                </span>
                <br />
                Personalized guidance on strategies, workflows, and getting the
                most out of Xynth&apos;s AI.
              </p>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleBookCall}
                disabled={booking}
                className="mt-8 w-full max-w-[320px] flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-xl bg-white text-black text-[15px] font-semibold hover:bg-zinc-100 transition-colors disabled:opacity-50"
              >
                {booking ? (
                  <>
                    <div className="w-4 h-4 rounded-full border-2 border-black/20 border-t-black animate-spin" />
                    Confirming...
                  </>
                ) : (
                  "Book your trading session"
                )}
              </motion.button>

              <button
                type="button"
                onClick={handleDismiss}
                className="mt-3 text-[13px] text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                Continue to Xynth
              </button>

              <p className="mt-4 text-[12px] text-zinc-600">
                Included with Elite
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
